import { TableCellHeaderStates, TableCellNode } from "@lexical/table";
import { makeColumnPaddings, makePx } from "../../../email-design/util";
import { $convertStylableTableCellElement } from "./stylable-table-cell-utils";

import type { StyleableColumnConfig } from "../utils/use-styleable-columns";
import type { SerializedTableCellNode } from "@lexical/table";
import type {
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from "lexical";

export type SerializedStylableTableCellNode = Spread<
  {
    custom_padding: {
      left: number;
      right: number;
      top: number;
      bottom: number;
      common: number;
      method: "individual" | "common";
    };
    version: 100;
  },
  SerializedTableCellNode
>;

export class StylableTableCellNode extends TableCellNode {
  /**
   * An existing table cell will not have this defined at all.
   * Once the rendered cell is adjusted, this will be defined.
   */
  __custom_padding?: {
    left: number;
    right: number;
    top: number;
    bottom: number;
    common: number;
    method: "individual" | "common";
  };

  static getType(): string {
    return "tablecell";
  }

  static clone(node: StylableTableCellNode): StylableTableCellNode {
    return new this(node.__headerState, node.__colSpan, node.__width, node.__key);
  }

  afterCloneFrom(node: this): void {
    super.afterCloneFrom(node);
    if (node.__custom_padding) {
      this.__custom_padding = { ...node.__custom_padding };
    }
  }

  static importJSON(
    serializedNode: SerializedStylableTableCellNode | SerializedTableCellNode,
  ): StylableTableCellNode {
    const node = new this().updateFromJSON(serializedNode);
    if ("custom_padding" in serializedNode && serializedNode.version === 100) {
      node.__custom_padding = {
        left: serializedNode.custom_padding.left,
        right: serializedNode.custom_padding.right,
        top: serializedNode.custom_padding.top,
        bottom: serializedNode.custom_padding.bottom,
        common: serializedNode.custom_padding.common,
        method: serializedNode.custom_padding.method,
      };
    }

    return node;
  }
  exportJSON(): SerializedStylableTableCellNode | SerializedTableCellNode {
    if (!this.__custom_padding) {
      // This is an unmodified table cell node.
      return super.exportJSON();
    }
    // This has been customised.
    return {
      ...super.exportJSON(),
      custom_padding: {
        left: this.__custom_padding?.left ?? 0,
        right: this.__custom_padding?.right ?? 0,
        top: this.__custom_padding?.top ?? 0,
        bottom: this.__custom_padding?.bottom ?? 0,
        common: this.__custom_padding?.common ?? 0,
        method: this.__custom_padding?.method ?? "individual",
      },
      version: 100,
    };
  }

  createDOM(config: EditorConfig): HTMLTableCellElement {
    const dom = super.createDOM(config);
    if (this.__backgroundColor) {
      dom.style.backgroundColor = this.__backgroundColor;
    }
    if (this.__custom_padding) {
      dom.classList.add("customised-padding");
      if (this.__custom_padding.method === "common") {
        dom.style.padding = makePx(this.__custom_padding.common);
      } else {
        dom.style.paddingLeft = makePx(this.__custom_padding.left);
        dom.style.paddingRight = makePx(this.__custom_padding.right);
        dom.style.paddingTop = makePx(this.__custom_padding.top);
        dom.style.paddingBottom = makePx(this.__custom_padding.bottom);
      }
    }

    return dom;
  }

  // It's not possible to alter the table cell dom for this so we have to recreate it.
  updateDOM(): boolean {
    return true;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const dom = super.exportDOM(editor);
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const tableCellElement = dom.element as HTMLTableCellElement;
    if (this.__custom_padding) {
      tableCellElement.classList.add("stylable-table-cell");
      if (this.__custom_padding.method === "common") {
        tableCellElement.style.padding = makePx(this.__custom_padding.common);
      } else {
        tableCellElement.style.paddingLeft = makePx(this.__custom_padding.left);
        tableCellElement.style.paddingRight = makePx(this.__custom_padding.right);
        tableCellElement.style.paddingTop = makePx(this.__custom_padding.top);
        tableCellElement.style.paddingBottom = makePx(this.__custom_padding.bottom);
      }
    }

    return dom;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      td: (node) => ({
        conversion: () => $convertStylableTableCellElement(node, $createStylableTableCellNode),
        priority: 0,
      }),
      th: (node) => ({
        conversion: () => $convertStylableTableCellElement(node, $createStylableTableCellNode),
        priority: 0,
      }),
    };
  }

  setCommonPadding(padding = 0): void {
    const writable = this.getWritable();
    writable.__custom_padding = {
      left: padding,
      right: padding,
      top: padding,
      bottom: padding,
      common: padding,
      method: "common",
    };
  }

  setIndividualPadding({
    left = this.__custom_padding?.left ?? 0,
    right = this.__custom_padding?.right ?? 0,
    top = this.__custom_padding?.top ?? 0,
    bottom = this.__custom_padding?.bottom ?? 0,
  }: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  }): void {
    const writable = this.getWritable();
    const common = Math.max(left, right, top, bottom);
    writable.__custom_padding = {
      left,
      right,
      top,
      bottom,
      common,
      method: "individual",
    };
  }
}

function $createStylableTableCellNode(
  styleableColumnConfig?: StyleableColumnConfig | null,
  headerState?: number,
  colSpan?: number,
  width?: number,
  key?: NodeKey,
): StylableTableCellNode {
  const tableCellNode = new StylableTableCellNode(
    headerState ?? TableCellHeaderStates.NO_STATUS,
    colSpan ?? 1,
    width,
    key,
  );
  if (
    !styleableColumnConfig?.column_individual_padding &&
    typeof styleableColumnConfig?.column_padding === "number"
  ) {
    tableCellNode.setCommonPadding(styleableColumnConfig.column_padding);
  }
  if (styleableColumnConfig?.column_individual_padding) {
    const paddings = makeColumnPaddings(styleableColumnConfig);
    tableCellNode.setIndividualPadding(paddings);
  }

  return tableCellNode;
}

export function $isStylableTableCellNode(
  node: LexicalNode | null | undefined,
): node is StylableTableCellNode {
  return node instanceof StylableTableCellNode;
}
